/**
 * CADUploadZone - Drag & drop file upload component for CAD files
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Alert,
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { uploadCADFile } from '../../services/cad.service';
import { CADModel } from '../../types/cad';

const ACCEPTED_EXTENSIONS = ['.stp', '.step', '.stl', '.igs', '.iges'];

interface CADUploadZoneProps {
  onUploadComplete: (model: CADModel) => void;
}

const CADUploadZone: React.FC<CADUploadZoneProps> = ({ onUploadComplete }) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      return t('cad.upload.unsupportedFormat', {
        extension: ext,
        formats: ACCEPTED_EXTENSIONS.join(', '),
      });
    }
    if (file.size > 100 * 1024 * 1024) {
      return t('cad.upload.fileTooLarge');
    }
    return null;
  }, [t]);

  const handleUpload = useCallback(async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setUploading(true);
    setProgress(0);

    try {
      const model = await uploadCADFile(file, (p) => setProgress(p));
      onUploadComplete(model);
    } catch (err: any) {
      setError(err?.message || err?.detail || t('cad.upload.failed'));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [onUploadComplete, t, validateFile]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleUpload(file);
    },
    [handleUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  return (
    <Box>
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
        sx={{
          border: '2px dashed',
          borderColor: isDragOver ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: uploading ? 'default' : 'pointer',
          bgcolor: isDragOver ? 'action.hover' : 'transparent',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.light',
            bgcolor: 'action.hover',
          },
          opacity: uploading ? 0.6 : 1,
          pointerEvents: uploading ? 'none' : 'auto',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {t('cad.upload.dropLabel')}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          STEP (.stp/.step) / STL (.stl) / IGES (.igs/.iges)
        </Typography>
      </Box>

      {uploading && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('cad.upload.processing', { progress })}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default CADUploadZone;
